import { useState } from "react";
import { Link } from "react-router";
import { usePageParam } from "@/hooks/use-page-param";
import { ListPagination } from "@/components/ui/list-pagination";
import { AlertTriangle, Building2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { SearchInput } from "@/components/ui/search-input";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { EmpresaEditModal } from "@/components/empresas/EmpresaEditModal";
import { parseLinkedInUrl } from "@/utils/external-url";

interface Empresa {
  id: string;
  nome: string;
  slug: string | null;
  segmento: string | null;
  tipo: string | null;
  cidade: string | null;
  website: string | null;
  linkedin_url: string | null;
  descricao: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

type FilterSegmento = "todos" | "vazio" | "preenchido";

/** URL do LinkedIn como texto secundário, marcando quando não é navegável. */
function EmpresaLinkedInText({ url }: { url: string }) {
  const parsed = parseLinkedInUrl(url);
  if (!parsed.warning) {
    return <div className="text-xs text-muted-foreground truncate max-w-md">{url}</div>;
  }
  const isInvalid = parsed.status === "invalid";
  return (
    <div
      className={`flex items-center gap-1 text-xs truncate max-w-md ${
        isInvalid ? "text-destructive" : "text-muted-foreground"
      }`}
      title={parsed.warning}
    >
      <AlertTriangle
        className={`h-3 w-3 shrink-0 ${isInvalid ? "text-destructive" : "text-amber-500"}`}
      />
      <span className="truncate">{url}</span>
    </div>
  );
}

export default function EmpresasPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [tipo, setTipo] = useState<string>("todos");
  const [segmentoFilter, setSegmentoFilter] = useState<FilterSegmento>("todos");
  const [editing, setEditing] = useState<Empresa | null>(null);

  const { data: empresas = [], isLoading, isError, refetch } = useQuery({
    queryKey: ["empresas-list", search, tipo, segmentoFilter],
    queryFn: async () => {
      let q = supabase.from("empresas" as never).select("*").order("nome");
      if (search.length >= 2) {
        q = q.ilike("nome", `%${search}%`);
      }
      if (tipo !== "todos") {
        q = q.eq("tipo", tipo);
      }
      if (segmentoFilter === "vazio") {
        q = q.is("segmento", null);
      } else if (segmentoFilter === "preenchido") {
        q = q.not("segmento", "is", null);
      }
      const { data, error } = await q.limit(500);
      if (error) throw error;
      return (data as unknown as Empresa[]) ?? [];
    },
  });

  const totalVazias = empresas.filter((e) => !e.segmento).length;

  const [currentPage, setCurrentPage] = usePageParam();
  const PAGE_SIZE = 30;
  const totalPages = Math.max(1, Math.ceil(empresas.length / PAGE_SIZE));
  const paginaEfetiva = Math.min(currentPage, totalPages);
  const empresasPagina = empresas.slice(
    (paginaEfetiva - 1) * PAGE_SIZE,
    paginaEfetiva * PAGE_SIZE,
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Empresas"
        subtitle={
          <p className="mt-1 text-sm text-muted-foreground">
            Empresas extraídas do LinkedIn ou cadastradas manualmente.
            {totalVazias > 0 && (
              <span className="ml-2">
                <Badge variant="outline">{totalVazias} sem segmento</Badge>; clique pra preencher.
              </span>
            )}
          </p>
        }
      />

      <FilterToolbar>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Buscar por nome..."
        />
        <Select value={tipo} onValueChange={setTipo}>
          <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os tipos</SelectItem>
            <SelectItem value="agencia">Agência</SelectItem>
            <SelectItem value="cliente">Cliente</SelectItem>
            <SelectItem value="marca">Marca</SelectItem>
            <SelectItem value="evento">Evento</SelectItem>
            <SelectItem value="studio">Studio</SelectItem>
            <SelectItem value="outro">Outro</SelectItem>
          </SelectContent>
        </Select>
        <Select value={segmentoFilter} onValueChange={(v) => setSegmentoFilter(v as FilterSegmento)}>
          <SelectTrigger className="w-full sm:w-[200px]"><SelectValue placeholder="Segmento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="vazio">Sem segmento (curar)</SelectItem>
            <SelectItem value="preenchido">Com segmento</SelectItem>
          </SelectContent>
        </Select>
      </FilterToolbar>

      {isLoading ? (
        <LoadingState variant="list" />
      ) : isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : empresas.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Nenhuma empresa encontrada"
          description={
            search || tipo !== "todos" || segmentoFilter !== "todos"
              ? "Ajuste a busca ou os filtros para ver mais resultados."
              : "As empresas de PDFs do LinkedIn ou cadastradas manualmente aparecem aqui."
          }
        />
      ) : (
        <Card>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Segmento</TableHead>
                  <TableHead>Cidade</TableHead>
                  <TableHead>Curadoria</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {empresasPagina.map((emp) => {
                  const curatedAt = (emp.metadata as Record<string, string> | null)?.curated_at;
                  return (
                    <TableRow key={emp.id} className="hover:bg-muted/40">
                      <TableCell>
                        <Link
                          to={`/empresas/${emp.id}`}
                          className="font-medium text-sm hover:underline"
                        >
                          {emp.nome}
                        </Link>
                        {emp.linkedin_url && (
                          <EmpresaLinkedInText url={emp.linkedin_url} />
                        )}
                      </TableCell>
                      <TableCell>
                        {emp.tipo ? <Badge variant="outline">{emp.tipo}</Badge> : <span className="text-xs text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        {emp.segmento ? (
                          <span className="text-sm">{emp.segmento}</span>
                        ) : (
                          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">vazio</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{emp.cidade ?? "-"}</TableCell>
                      <TableCell>
                        {curatedAt ? (
                          <Badge variant="outline" className="bg-green-50 text-green-800">curada</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-blue-50 text-blue-800">IA</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing(emp)}
                        >
                          Editar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && totalPages > 1 && (
        <ListPagination
          currentPage={paginaEfetiva}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      <EmpresaEditModal
        empresa={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          toast({ title: "Empresa atualizada" });
          queryClient.invalidateQueries({ queryKey: ["empresas-list"] });
          setEditing(null);
        }}
      />
    </div>
  );
}
